import { EventIds } from 'constants/eventIds';
import { useCallback } from 'react';
import { log } from 'utils/log';
import { useUpdateItemFavoriteStatus } from 'hooks/useUpdateItemFavoriteStatus';
import { useSessionStorageContext } from 'contexts/SessionStorageContext/SessionStorageContext';
import { useSelectedSamplesContext } from 'contexts/SelectedSamplesContext/SelectedSamplesContext';
import { usePurchaseControllerContext } from 'contexts/PurchaseControllerContext/PurchaseControllerContext';
import {
    AuthenticatedActions,
    AuthenticatedUserActionArgs,
    AnyAuthenticatedUserAction,
    DownloadableItem,
} from 'types';
import { useApolloClient } from '@apollo/client';
import {
    RequestCostPreviewQueries,
    RequestCostPreviewQueryItemName,
} from 'apollo/cost';
import { DownloadType } from 'types/enums';
import { useFreeUserFreeDownloadUpsellModalContext } from 'modals/UpsellModals/FreeUserFreeDownloadUpsellModalContext';
import { useModalContext } from 'contexts/ModalContext/ModalContext';

type HandleAttemptedActionType = (
    attemptedAction: AnyAuthenticatedUserAction,
    upsellingAllowed: boolean,
) => void;

export const useHandleAttemptedAction = (): HandleAttemptedActionType => {
    const apolloClient = useApolloClient();
    const { openModal: openFreeUserFreeDownloadUpsellModal } =
        useFreeUserFreeDownloadUpsellModalContext();
    const { startPurchase } = usePurchaseControllerContext();
    const { toggleAllSamplesSelection } = useSelectedSamplesContext();
    const { getSamplesToAddToCollection } = useSessionStorageContext();
    const { updateFavorite } = useUpdateItemFavoriteStatus();
    const { openModal } = useModalContext();

    const handleDownloadAction = useCallback(
        (item: DownloadableItem, upsellingAllowed: boolean) => {
            async function execute() {
                const shouldUseId = [
                    DownloadType.Pack,
                    DownloadType.Repack,
                    DownloadType.Collection,
                    DownloadType.CollectionMulti,
                ].includes(item.type);

                const { data } = await apolloClient.query({
                    query: RequestCostPreviewQueries[item.type],
                    fetchPolicy: 'no-cache',
                    variables: {
                        guid: shouldUseId ? item.id : item.itemIds,
                    },
                });

                const costPreviewData =
                    data[RequestCostPreviewQueryItemName[item.type]];

                const isFree = costPreviewData.credits === 0;

                if (upsellingAllowed && isFree) {
                    openFreeUserFreeDownloadUpsellModal(item);
                } else {
                    startPurchase({ item });
                }
            }

            execute();
        },
        [apolloClient, startPurchase, openFreeUserFreeDownloadUpsellModal],
    );

    return useCallback(
        (attemptedAction, upsellingAllowed) => {
            const { args } = attemptedAction;
            let item, samplesToAddToCollection;

            if (!Array.isArray(args)) {
                log.warn(
                    `An action, ${attemptedAction.type}, was passed to the post-login action handler but no arguments were provided`,
                    EventIds.NoArgumentsProvidedToPostLoginAction,
                );
                return;
            }

            switch (attemptedAction.type) {
                case AuthenticatedActions.downloadItem:
                    [item] =
                        args as AuthenticatedUserActionArgs[AuthenticatedActions.downloadItem];

                    handleDownloadAction(item, upsellingAllowed);

                    break;
                case AuthenticatedActions.updateItemFavoriteState:
                    [item] =
                        args as AuthenticatedUserActionArgs[AuthenticatedActions.updateItemFavoriteState];

                    updateFavorite(item);
                    break;
                case AuthenticatedActions.addSamplesToCollection:
                    samplesToAddToCollection = getSamplesToAddToCollection();

                    if (samplesToAddToCollection.length) {
                        toggleAllSamplesSelection(samplesToAddToCollection);
                    }
                    break;
                case AuthenticatedActions.openTrialUpsellModal:
                    openModal.trialUpsellModal();
                    break;
                default:
                    log.warn(
                        `An unsupported action, ${attemptedAction.type}, was passed to the post-login action handler.`,
                        EventIds.UnsupportedPostLoginAction,
                    );
                    break;
            }
        },
        [
            updateFavorite,
            getSamplesToAddToCollection,
            toggleAllSamplesSelection,
            handleDownloadAction,
        ],
    );
};

export default useHandleAttemptedAction;
